import React, { useState } from 'react';
import { Users, Sparkles, GraduationCap, LineChart, ArrowRight, Check, ChevronRight, Star } from 'lucide-react';
import { Button } from './ui/button';
import { services } from '../data/mock';

const iconMap = {
  Users: Users,
  Sparkles: Sparkles,
  GraduationCap: GraduationCap,
  LineChart: LineChart
};

const Services = () => {
  const [activeService, setActiveService] = useState(0);

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-28 bg-gradient-to-b from-white via-slate-50/50 to-white relative overflow-hidden">
      {/* Premium Background Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-shsTeal/30 to-transparent" />
      <div className="absolute top-20 left-0 w-[500px] h-[500px] bg-shsTeal/5 rounded-full blur-[100px]" />
      <div className="absolute bottom-20 right-0 w-[500px] h-[500px] bg-shsGold/5 rounded-full blur-[100px]" />
      
      {/* Decorative Elements */}
      <div className="absolute top-40 right-20 w-3 h-3 bg-shsTeal rounded-full animate-pulse" />
      <div className="absolute bottom-40 left-20 w-2 h-2 bg-shsGold rounded-full animate-pulse delay-300" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-shsTeal/10 to-shsBlue/10 rounded-full text-sm font-semibold mb-6 border border-shsTeal/20">
            <Sparkles className="w-4 h-4 text-shsTeal" />
            <span className="text-shsTeal">Our Expertise</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-shsNavy mb-6 leading-tight">
            Premium Housekeeping
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-shsTeal via-emerald-500 to-shsBlue">
              Solutions
            </span>
          </h2>
          <p className="text-gray-600 text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed">
            We specialize in delivering exceptional housekeeping services that exceed 
            5-star international standards.
          </p>
        </div>

        {/* Services Cards - Premium Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const isFirst = index === 0;
            
            return (
              <div
                key={service.id}
                className={`group relative rounded-3xl transition-all duration-500 overflow-hidden ${
                  isFirst 
                    ? 'bg-gradient-to-br from-shsTeal via-shsBlue to-shsNavy text-white shadow-2xl shadow-shsTeal/30 lg:scale-105' 
                    : 'bg-white hover:shadow-2xl hover:shadow-shsTeal/15 border border-gray-100 hover:border-shsTeal/30'
                }`}
              >
                {/* Shine Effect on Hover */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ${
                  isFirst ? 'bg-gradient-to-r from-transparent via-white/10 to-transparent' : 'bg-gradient-to-r from-transparent via-shsTeal/5 to-transparent'
                }`} />
                
                <div className="relative p-8">
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 ${
                    isFirst 
                      ? 'bg-white/20 group-hover:bg-white/30' 
                      : 'bg-gradient-to-br from-shsTeal/10 to-shsBlue/10 group-hover:from-shsTeal group-hover:to-shsBlue group-hover:scale-110'
                  }`}>
                    {IconComponent && (
                      <IconComponent className={`w-8 h-8 transition-colors duration-300 ${
                        isFirst ? 'text-white' : 'text-shsTeal group-hover:text-white'
                      }`} />
                    )}
                  </div>

                  {/* Content */}
                  <h3 className={`text-xl font-bold mb-3 ${isFirst ? 'text-white' : 'text-shsNavy'}`}>
                    {service.title}
                  </h3>
                  <p className={`text-sm mb-6 leading-relaxed ${isFirst ? 'text-white/80' : 'text-gray-600'}`}>
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-sm">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                          isFirst ? 'bg-shsGold/20' : 'bg-shsTeal/10'
                        }`}>
                          <Check className={`w-3 h-3 ${isFirst ? 'text-shsGold' : 'text-shsTeal'}`} />
                        </div>
                        <span className={isFirst ? 'text-white/90' : 'text-gray-600'}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Learn More */}
                  <button 
                    onClick={scrollToContact}
                    className={`inline-flex items-center gap-1 text-sm font-bold hover:gap-3 transition-all duration-300 ${
                      isFirst ? 'text-shsGold hover:text-white' : 'text-shsTeal hover:text-shsBlue'
                    }`}
                  >
                    Get Started
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Badge for first item */}
                {isFirst && (
                  <div className="absolute -top-0 -right-0">
                    <div className="bg-shsGold text-shsNavy text-xs font-bold px-4 py-2 rounded-bl-2xl rounded-tr-3xl shadow-lg">
                      <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 fill-shsNavy" />
                        Most Popular
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Premium CTA Section */}
        <div className="mt-24">
          <div className="relative rounded-3xl overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-shsNavy via-shsBlue to-shsNavy" />
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-96 h-96 bg-shsTeal rounded-full blur-[100px]" />
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-shsGold rounded-full blur-[100px]" />
            </div>
            
            <div className="relative px-8 py-16 sm:px-16 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6 border border-white/20">
                <Sparkles className="w-4 h-4 text-shsGold" />
                <span className="text-white/90 text-sm font-medium">Free Consultation</span>
              </div>
              
              <h3 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to Elevate Your Housekeeping?
              </h3>
              <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">
                Join leading hotels across Australia who trust Supreme Hospitality Solutions 
                for their housekeeping excellence.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={scrollToContact}
                  className="bg-shsGold hover:bg-white text-shsNavy px-10 py-7 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-shsGold/30"
                >
                  Contact Us Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <p className="text-white/50 text-sm">Response within 24 hours</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
